import { useProductFetcher } from 'components/WorkspaceEditor/api/useProductFetcher/useProductFetcher'
import { useProductFetcherUtils } from 'components/WorkspaceEditor/api/useProductFetcher/useProductFetcher.utils'
import {
    GetProjectNuggetContentViewResponse,
    GetProjectProductFamilyViewResponse,
} from 'components/WorkspaceEditor/types'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { Dispatch, SetStateAction, useCallback } from 'react'
import { CONTENT_FETCH_STATES } from './useScrollRequestExecutor'

export type TabName = 'general' | 'products' | 'execution'

export const tabIndex: Record<TabName, number> = {
    general: 1,
    products: 2,
    execution: 3,
}

/**
 * Hook for handling active tab changes and navigation.
 *
 * Coordinates with content loading to prevent race conditions by setting
 * the coordination flag when tab switch is complete.
 *
 * @param setIsReadyForContentFetch - State setter to signal when content loading can begin
 */
export const useHandleActiveTab = (
    setIsReadyForContentFetch: Dispatch<SetStateAction<boolean>>
) => {
    const { setIsTreeLoading, activeTab, setActiveTab } = useShallowSelector(
        (state) => ({
            setIsTreeLoading: state.actions.setIsTreeLoading,
            activeTab: state.activeTab,
            setActiveTab: state.actions.setActiveTab,
        })
    )
    const { onContentViewFetchSuccess } = useProductFetcherUtils({
        overrideContentItemNodes: true,
    })
    const { getContentItemViewNodesAPI } = useProductFetcher({
        overrideContentItemNodes: true,
        callback: async (
            response:
                | GetProjectProductFamilyViewResponse
                | GetProjectNuggetContentViewResponse
        ) => {
            await onContentViewFetchSuccess(response, [], false)
            // Signal that content loading can now begin (prevents race condition)
            setIsReadyForContentFetch(CONTENT_FETCH_STATES.READY_TO_FETCH)
            setIsTreeLoading(false)
        },
    })

    const handleTrackChangeFromActiveTab = useCallback(
        async (trackChangeTab: string) => {
            // Returns true if the track change belongs to the currently active tab
            const tab = trackChangeTab.toLowerCase() as TabName

            if (tabIndex[tab] === activeTab) {
                // Already on correct tab, signal that content loading can begin
                setIsReadyForContentFetch(CONTENT_FETCH_STATES.READY_TO_FETCH)
                return true
            }

            try {
                setActiveTab(tabIndex[tab])
                setIsTreeLoading(true)
                await getContentItemViewNodesAPI([], tabIndex[tab])
                return true
            } catch (error) {
                console.error(
                    `Error in handleTrackChangeFromActiveTab: ${error}`
                )
                // Reset coordination flag on error to prevent stuck state
                setIsReadyForContentFetch(CONTENT_FETCH_STATES.FETCH_COMPLETE)
                setIsTreeLoading(false)
                return false
            }
        },
        [
            activeTab,
            tabIndex,
            getContentItemViewNodesAPI,
            setIsTreeLoading,
            setActiveTab,
            setIsReadyForContentFetch,
        ]
    )

    return handleTrackChangeFromActiveTab
}
